import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { LeftContent, RightContent } from "../../global-components/src/components/screen/left-content";

const features = [
    {
        emoji: "💬",
        title: "Iniciar conversas",
        description: "Não sabe como falar com alguém? O Paçoca AI sugere uma abertura de papo personalizada com base no perfil da pessoa.",
    },
    {
        emoji: "🎯",
        title: "Compatibilidade",
        description: "Descobre o quanto você combina com outros usuários — afinidades, gostos e personalidade em comum.",
    },
    {
        emoji: "👥",
        title: "Recomendação de pessoas",
        description: "Sugere perfis interessantes para você seguir com base no que você curte e no seu jeito de usar a rede.",
    },
    {
        emoji: "🔥",
        title: "Roast do perfil",
        description: "Quer levar um julgamento sincero (e engraçado) do seu perfil? O Paçoca AI analisa e não poupa.",
    },
    {
        emoji: "🪞",
        title: "Resumo de personalidade",
        description: "Uma leitura do seu perfil: quem você é, o que seus posts dizem sobre você e qual é a sua vibe na rede.",
    },
    {
        emoji: "✨",
        title: "Análise de vibe",
        description: "Qual é a estética, energia e aura do seu perfil? O AI lê os sinais e te diz.",
    },
    {
        emoji: "🎬",
        title: "Recomendações culturais",
        description: "Filmes, séries, músicas, animes, jogos ou livros — o Paçoca AI recomenda com base no seu gosto.",
    },
    {
        emoji: "📝",
        title: "Dicas de perfil",
        description: "Quer melhorar sua bio ou a estética do seu perfil? O AI dá sugestões práticas pra você se destacar.",
    },
    {
        emoji: "⏰",
        title: "Melhor hora para postar",
        description: "Analisa o comportamento do seu perfil e indica quando seus posts têm mais chance de engajar.",
    },
    {
        emoji: "💡",
        title: "Ideias de post",
        description: "Travou na criatividade? O Paçoca AI sugere ideias de conteúdo alinhadas ao seu estilo.",
    },
    {
        emoji: "📊",
        title: "Análise de público",
        description: "Entenda quem te segue, quem curte seus posts e como é a audiência do seu perfil.",
    },
    {
        emoji: "🌐",
        title: "Insights da plataforma",
        description: "O que está bombando no Paçoca? Tendências, assuntos em alta e comportamentos da comunidade.",
    },
    {
        emoji: "📚",
        title: "Hábitos de leitura",
        description: "O AI analisa seu histórico de leitura e te conta sobre o seu perfil de leitor.",
    },
    {
        emoji: "🚀",
        title: "Criar posts",
        description: "Diga o que quer postar e o AI cria e publica direto — sem você precisar sair da conversa.",
    },
    {
        emoji: "✉️",
        title: "Enviar mensagens",
        description: "Manda mensagem pra alguém só falando com o AI: ele escreve e envia por você.",
    },
];

const PacocaAiPage = () => {
    const siteUrl = "https://pacoca.net";
    const pageUrl = `${siteUrl}/blog/pacoca-ai`;

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: "Paçoca AI — o que ela pode fazer por você",
        description:
            "Conheça todas as funcionalidades do Paçoca AI: recomendações, análise de perfil, ideias de post e muito mais.",
        url: pageUrl,
        publisher: { "@type": "Organization", name: "Paçoca Inc.", url: siteUrl },
        inLanguage: "pt-BR",
        image: `${siteUrl}/img/link-preview.png?v=1`,
    };

    return (
        <>
            <LeftContent/>
            <RightContent/>
            <Helmet>
                <title>Paçoca AI – O que ela pode fazer</title>
                <meta
                    name="description"
                    content="Conheça o Paçoca AI: análise de perfil, compatibilidade, recomendações, ideias de post, roast e muito mais — tudo integrado à rede social brasileira."
                />
                <meta
                    name="keywords"
                    content="paçoca ai, inteligência artificial rede social, ia brasileira, paçoca inteligência artificial, assistente social"
                />
                <meta name="author" content="Paçoca Inc." />
                <link rel="canonical" href={pageUrl} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content="Paçoca AI – O que ela pode fazer" />
                <meta property="og:description" content="Tudo o que o Paçoca AI consegue fazer pela sua experiência na rede social." />
                <meta property="og:image" content={`${siteUrl}/img/link-preview.png?v=1`} />
                <meta property="og:locale" content="pt_BR" />
                <meta property="og:site_name" content="Paçoca" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Paçoca AI – O que ela pode fazer" />
                <meta name="twitter:description" content="Tudo o que o Paçoca AI consegue fazer pela sua experiência na rede social." />
                <meta name="twitter:image" content={`${siteUrl}/img/link-preview.png?v=1`} />
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            <div className="blog-post-page">
                <div className="blog-post-eyebrow">
                    <Link to="/blog">Blog</Link>
                    <span aria-hidden="true">›</span>
                    <span>Paçoca AI</span>
                </div>

                <h1 className="blog-post-title">O que o Paçoca AI pode fazer</h1>

                <p className="blog-post-lead">
                    Uma IA integrada direto na rede social — pra você entender melhor seu perfil,
                    descobrir pessoas novas e turbinar sua presença no Paçoca.
                </p>

                <hr className="blog-post-divider" />

                <div className="ai-features-grid">
                    {features.map((f, i) => (
                        <div className="ai-feature-card" key={i}>
                            <span className="ai-feature-emoji" aria-hidden="true">{f.emoji}</span>
                            <h3 className="ai-feature-title">{f.title}</h3>
                            <p className="ai-feature-desc">{f.description}</p>
                        </div>
                    ))}
                </div>

                <hr className="blog-post-divider" />

                <p className="ai-footer-note">
                    O Paçoca AI está disponível para todos os usuários da rede. Basta abrir o chat
                    e perguntar — ele entende linguagem natural e já sabe quem você é.
                </p>
            </div>
        </>
    );
};

export default PacocaAiPage;